import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Box,
  Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { toast, Toaster } from 'react-hot-toast';
import { AssetDetails } from '../Publish/AssetDetails';
import { ChevronDown, ChevronRight } from 'lucide-react';
import heartLoading from "../../../assests/heartLoading.gif";
import preloader from "../../../assests/preloader1.gif";
import { Edit, Delete } from "@mui/icons-material";
import EditAlertModal from './EditAlertModal';


// View-specific ProductDetails component (excludes Costs)
const ProductDetailsView = ({ publishData }) => {
  if (!publishData?.ProductInfo) {
    return <div className="text-gray-600">No product information available.</div>;
  }

  const productInfo = publishData.ProductInfo;

  // Basic Information fields (excluding costs)
  const basicFields = [
    { key: 'productName', label: 'Product Name' },
    { key: 'productCode', label: 'Product Code' },
    { key: 'description', label: 'Description' },
    { key: 'productOwner', label: 'Product Owner' },
    { key: 'parentDomainName', label: 'Parent Domain' },
    { key: 'domainName', label: 'Domain' },
    { key: 'subDomainName', label: 'Sub Domain' },
    { key: 'dataLayer', label: 'Data Layer' },
    { key: 'productTag', label: 'Product Tags', specialRender: (value) => {
      if (!value) return 'N/A';
      const tags = Array.isArray(value) ? value : [value];
      const tagColors = [
        'bg-blue-100 text-blue-800', 'bg-green-100 text-green-800',
        'bg-purple-100 text-purple-800', 'bg-yellow-100 text-yellow-800',
        'bg-red-100 text-red-800', 'bg-indigo-100 text-indigo-800',
        'bg-pink-100 text-pink-800', 'bg-gray-100 text-gray-800',
        'bg-orange-100 text-orange-800', 'bg-teal-100 text-teal-800'
      ];
      return (
        <div className="flex flex-wrap gap-2">
          {tags.map((tag, index) => (
            <span
              key={index}
              className={`px-2 py-1 rounded-full text-xs font-medium ${tagColors[index % tagColors.length]}`}
            >
              {tag}
            </span>
          ))}
        </div>
      );
    }},
    { key: 'productDocumentationUrl', label: 'Documentation URL' },
    { key: 'status', label: 'Status' }
  ];

  // Market Information fields
  const marketFields = [
    { key: 'marketRegion', label: 'Market Region' },
    { key: 'marketCountry', label: 'Market Country' },
    { key: 'lineOfBusiness', label: 'Line of Business' }
  ];

  // Additional Information fields
  const additionalFields = [
    { key: 'minExpectedUsers', label: 'Min Expected Users' },
    { key: 'maxExpectedUsers', label: 'Max Expected Users' },
    { key: 'assetSource', label: 'Asset Source' },
    { key: 'sizeThreshold', label: 'Size Threshold', specialRender: (value) => {
      if (!value) return 'N/A';
      return `${value.value || 'N/A'} ${value.unit || ''}`;
    }},
    { key: 'valueMapping', label: 'Value Mapping' }
  ];

  const renderField = (field) => {
    const value = productInfo[field.key];
    
    if (field.specialRender) {
      return field.specialRender(value);
    }
    
    if (!value || (Array.isArray(value) && value.length === 0)) return 'N/A';
    
    // Handle array values for marketRegion and marketCountry
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    
    return value;
  };

  const renderSection = (title, fields) => (
    <div className="mb-8">
      {title !== 'Basic Information' && (
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>
      )}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white rounded-lg overflow-hidden shadow-sm">
          <thead className="bg-[#0000a0]">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Field</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Value</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {fields.map((field) => (
              <tr key={field.key} className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {field.label}
                </td>
                <td className="px-6 py-4 text-sm text-gray-700">
                  {renderField(field)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {renderSection('Basic Information', basicFields)}
      {renderSection('Market Information', marketFields)}
      {renderSection('Additional Information', additionalFields)}
    </div>
  );
};

const ProductViewModal = ({ open, onClose, product }) => {
  const [selectedSection, setSelectedSection] = useState('Product Details');
  const [expandedAssets, setExpandedAssets] = useState(false);
  const [loading, setLoading] = useState(false);
  const [publishData, setPublishData] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);

  useEffect(() => {
    if (open && product?.productId) {
      fetchPublishData();
    }
  }, [open, product?.productId]);

  const fetchPublishData = async () => {
    if (!product?.productId) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/publish-overview/${product.productId}`);
      setPublishData(response.data);
    } catch (error) {
      console.error('Error fetching publish data:', error);
      toast.error('Failed to load product details');
      setPublishData(null);
    } finally {
      setLoading(false);
    }
  };

  const toggleAssets = () => {
    setExpandedAssets(!expandedAssets);
  };

  const getAssetNames = () => {
    if (!publishData?.AssetInfo) return [];
    return Object.keys(publishData.AssetInfo).map(assetKey => ({
      key: assetKey,
      name: publishData.AssetInfo[assetKey].Configuration?.assetName || assetKey
    }));
  };

  const isAssetSelected = () => {
    return selectedSection.startsWith('asset-');
  };

  const getSelectedAssetKey = () => {
    if (isAssetSelected()) {
      return selectedSection.replace('asset-', '');
    }
    return null;
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="fixed inset-0 bg-white flex items-center justify-center z-50 w-full h-full">
                   <div className="flex flex-col items-center space-y-3">
                     <img src={preloader} alt="Loading..." width={200} />
                     <span className="text-gray-700 text-[20px]">
                     Hang tight! The resources are loading...
                     </span>
                   </div>
                 </div>
      );
    }

    if (isAssetSelected()) {
      const assetKey = getSelectedAssetKey();
      const assetData = publishData?.AssetInfo?.[assetKey];
      if (assetData) {
        return <AssetDetails publishData={{ AssetInfo: { [assetKey]: assetData } }} hideResults={true} />;
      }
    }

    switch (selectedSection) {
      case 'Product Details':
        return <ProductDetailsView publishData={publishData} />; // Custom view component - NO costs
      case 'SLA':
        return <SLAView publishData={publishData} />; // Custom component - NO alerts
      default:
        return <ProductDetailsView publishData={publishData} />;
    }
  };

  // SLA View Component (without alerts)
  const SLAView = ({ publishData }) => {
    if (!publishData?.SLAandAlerts) {
      return <div className="text-gray-600">No SLA information available.</div>;
    }

    const { slaResponseTime, slaUptime } = publishData.SLAandAlerts;
       
    const deleteAlert = async (alertId) => {
      setLoading(true);
      try {
        const response = await axios.delete(`${process.env.REACT_APP_API_URL}/alerts/${alertId}`);
        fetchPublishData();
        toast.success('Asset Alert deleted.');
      } catch (error) {
        toast.error('Failed to delete alert');
      } finally {
        setLoading(false);
      }
    }

    const handleViewAlert = (alert) => {
    if (alert) {
      setSelectedAlert(alert);
      setShowViewModal(true);
    }
  };

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-[#0000a0]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Metric</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Value</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Response Time</td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {slaResponseTime ? `${slaResponseTime} minutes` : 'N/A'}
                  </td>
                </tr>
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Uptime</td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {slaUptime ? `${slaUptime}%` : 'N/A'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        {Object.values(publishData?.AssetInfo || {}).some(asset => asset.AlertInfo?.length > 0) ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-[#0000a0]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Asset Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Alert Configuration</th>
                </tr>
              </thead>
              {Object.entries(publishData?.AssetInfo).map(([asset, info]) => (
              <tbody className="bg-white divide-y divide-gray-200">
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{asset}</td>
                  <td className="text-center text-sm text-gray-700">
                  {info.AlertInfo.length !== 0 ? (info.AlertInfo.map((alert) => (
                  <table className="bg-white divide-y divide-gray-200">
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{alert.alert_type} </td>
                      { alert.alert_type === "EMAIL" ?
                      (<td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        Recipients:  {JSON.parse(alert.alert_config).email}
                        </td>) :
                      (<td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        Channel:  {JSON.parse(alert.alert_config).channel_name} <br/>
                        Webhook:  {JSON.parse(alert.alert_config).webhook_link}
                      </td>)
                      }
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <IconButton onClick={() => handleViewAlert(alert)} size="small" sx={{ color: "#0000a0" }} >
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton onClick={() => deleteAlert(alert.alert_id)} size="small" sx={{ color: "#d21932ff" }} >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      </td>
                    </tr>
                  </table>
                  )))
                  :(
                    <span className='text-center'>No alerts configured.</span>
                  )}
                  </td>                  
                </tr>
              </tbody>
              ))}
            </table>
          </div>
        </div>
        ) : (<></>)
        }
          {showViewModal && (
                  <EditAlertModal
                    open={showViewModal}
                    onClose={() => {
                      setShowViewModal(false);
                      fetchPublishData();
                    }}
                    alert={selectedAlert}
                  />
                )}
      </div>
    );
  };

  if (!product) return null;

  const assetNames = getAssetNames();

  return (
    <>
      <Toaster position="top-right" />
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="xl" 
        fullWidth
        PaperProps={{
          sx: { 
            maxHeight: '90vh',
            width: '95%',
            maxWidth: '1400px'
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {product.productName || 'Product Details'}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
                {product.productCode || ''}
              </Typography>
            </Box>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ py: 3, px: 0 }}>
          <div className="flex">
            {/* Left Sidebar */}
            <div className="w-80 bg-white rounded-lg shadow-sm border border-gray-200 p-4 mr-0 flex-shrink-0">
              <div className="space-y-2">
                {/* Product Details */}
                <button
                  onClick={() => setSelectedSection('Product Details')}
                  className={`w-full text-left px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                    selectedSection === 'Product Details'
                      ? 'bg-[#0000a0] text-white'
                      : 'bg-white text-gray-900 border border-green-300 hover:bg-gray-50'
                  }`}
                >
                  Product Details
                </button>

                {/* Onboarded Assets - Parent */}
                <div>
                  <button
                    onClick={toggleAssets}
                    className={`w-full text-left px-4 py-3 rounded-lg font-medium transition-all duration-200 flex items-center justify-between ${
                      isAssetSelected()
                        ? 'bg-[#0000a0] text-white'
                        : 'bg-white text-gray-900 border border-green-300 hover:bg-gray-50'
                    }`}
                  >
                    <span>Onboarded Assets</span>
                    {expandedAssets ? (
                      <ChevronDown size={16} className="transform transition-transform duration-200" />
                    ) : (
                      <ChevronRight size={16} className="transform transition-transform duration-200" />
                    )}
                  </button>
                  
                  {/* Asset Sub-items */}
                  {expandedAssets && (
                    <div className="ml-4 mt-2 space-y-1">
                      {assetNames.map((asset) => (
                        <button
                          key={asset.key}
                          onClick={() => setSelectedSection(`asset-${asset.key}`)}
                          className={`w-full text-left px-4 py-2 rounded-lg text-sm transition-all duration-200 break-words ${
                            selectedSection === `asset-${asset.key}`
                              ? 'bg-[#0000a0] text-white'
                              : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          <span className="whitespace-normal leading-relaxed">{asset.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* SLA */}
                <button
                  onClick={() => setSelectedSection('SLA')}
                  className={`w-full text-left px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                    selectedSection === 'SLA'
                      ? 'bg-[#0000a0] text-white'
                      : 'bg-white text-gray-900 border border-green-300 hover:bg-gray-50'
                  }`}
                >
                  SLA and Alerts
                </button>
              </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 bg-white rounded-lg shadow-sm border border-gray-200 p-6 ml-0 overflow-y-auto" style={{ height: '600px' }}>
              {isAssetSelected() && (
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-gray-800">
                    {assetNames.find(asset => `asset-${asset.key}` === selectedSection)?.name || 'Asset Details'}
                  </h2>
                </div>
              )}
              {renderContent()}
            </div>
          </div>
        </DialogContent>

        <DialogActions sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}>
          <Button 
            onClick={onClose} 
            variant="contained" 
            sx={{ 
              px: 3,
              backgroundColor: '#0000a0',
              '&:hover': {
                backgroundColor: '#000080'
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProductViewModal;
